import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { customersApiService } from '../../services';

const ALL_BICOM_SERVERS = {
  'EPBX': [
    'epbx1.isncom.net',
    'epbx2.isncom.net',
    'epbx3.isncom.net',
    'epbx4.isncom.net',
    'epbx5.isncom.net',
    'epbx6.isncom.net',
    'epbx7.isncom.net',
    'epbx8.isncom.net',
    'epbx9.isncom.net',
    'epbx10.isncom.net',
    'epbx11.isncom.net',
    'epbx12.isncom.net',
    'epbx13.isncom.net',
    'epbx14.isncom.net',
    'epbx15.isncom.net',
  ],
  'INTELACLOUD': [
    'jax01.intelacloud.net',
    'jax02.intelacloud.net',
    'jax03.intelacloud.net',
    'mjia01.intelacloud.net',
    'mija01.intelacloud.net',
    'provoice.intelacloud.net',
  ],
  'JAX': [
    'jax10.isncom.net',
    'jaxhumane.isncom.net',
    'jaxhumane.socis.com.net',
  ],
  'JOON': [
    'joon2.isncom.net',
    'joon3.isncom.net',
    'joon4.isncom.net',
  ],
  'Miscellaneous': [
    'gateways.isncom.net',
    'gannon.isncom.net',
    'goog.isncom.net',
    'hyler.isncom.net',
    'jclewishq.isncom.net',
    'local.isncom.net',
    'msteam.isncom.net',
    'northflaped.isncom.net',
    'ns.isncom.net',
    'nsurance.isncom.net',
    'pstn.isncom.net',
    'pureddefensepest.isncom.net',
    'rem.isncom.net',
    'redoak.isncom.net',
    'test.isncom.net',
    'vma-notification.isncom.net',
    'volt.isncom.net',
    'welsh.isncom.net',
    'wxc.jfk.isncom.net',
    'wxc.sjc.isncom.net',
    'a2billing.isncom.net',
    'agetal.isncom.net',
    'agpm.isncom.net',
    'allegiance.isncom.net',
    'bulldog.isncom.net',
    'calleridfix.isncom.net',
    'ccdemo.isncom.net',
    'ccation.isncom.net',
    'efax.isncom.net',
    'egh.isncom.net',
    'faxback.isncom.net',
    'floridavein.isncom.net',
    'frc.epbx.isncom.net',
    'giannone.isncom.net',
  ],
};

function AddCustomers() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [values, setValues] = useState({
    customer_number: '',
    customer_name: '',
    edgemarc: 'no',
    em_mac_address: '',
    wan1: '',
    wan2: '',
    eth: '',
    company_address: '',
    platform: 'broadsoft',
    server: '',
    enterprise: '',
    isp_circuit_id: '',
  });

  const [selectedServerGroup, setSelectedServerGroup] = useState('EPBX');
  const [serverFilterText, setServerFilterText] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const filteredServers = useMemo(() => {
    const lowercasedFilter = serverFilterText.trim().toLowerCase();
    const servers = ALL_BICOM_SERVERS[selectedServerGroup] || [];
    
    if (!lowercasedFilter) {
      return servers;
    }

    return servers.filter(server => 
      server.toLowerCase().includes(lowercasedFilter)
    );
  }, [selectedServerGroup, serverFilterText]);

  useEffect(() => {
    if (values.server && serverFilterText) {
      const serverExists = filteredServers.includes(values.server);
      if (!serverExists) {
        setValues(prev => ({ ...prev, server: '' }));
      }
    }
  }, [serverFilterText, values.server, filteredServers]);

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      customersApiService.getCustomerById(id)
        .then(response => {
          // External API returns single object, not array
          const customer = Array.isArray(response) ? response[0] : response;
          setValues({
            ...customer,
            em_mac_address: customer.em_mac_address || '',
            wan1: customer.wan1 || '',
            wan2: customer.wan2 || '',
            eth: customer.eth || '',
            server: customer.server || '',
          });
          setIsLoading(false);
        })
        .catch(error => {
          console.error('Error fetching customer data:', error);
          setIsLoading(false);
        });
    }
  }, [id]);

  const handleChange = e => {
    const { name, value } = e.target;
    setValues(prevValues => ({ ...prevValues, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (id) {
        await customersApiService.updateCustomer(id, values);
        console.log('Customer updated successfully');
      } else {
        await customersApiService.createCustomer(values);
        console.log('Customer added successfully');
      }
      navigate('/customers');
    } catch (error) {
      console.error('Error saving customer:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-screen h-screen mx-auto pt-10 bg-greenDispatch p-6 shadow-md">
      <h2 className="text-2xl font-semibold text-gray-700 mb-4">
        {id ? 'Edit Customer' : 'Add Customer'}
      </h2>
      <form onSubmit={handleSubmit}>
        {/* Customer Number */}
        <div className="mb-4">
          <label htmlFor="customer_number" className="block text-gray-700 font-medium">Customer Number</label>
          <input
            id="customer_number"
            type="text"
            name="customer_number"
            value={values.customer_number}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded-md shadow-sm"
            placeholder="100XXXXX"
            required
          />
        </div>

        {/* Customer Name */}
        <div className="mb-4">
          <label htmlFor="customer_name" className="block text-gray-700 font-medium">Customer Name</label>
          <input
            id="customer_name"
            type="text"
            name="customer_name"
            value={values.customer_name}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded-md shadow-sm"
            placeholder="Customer company name"
            required
          />
        </div>

        {/* Edgemarc */}
        <div className="mb-4">
          <label htmlFor="edgemarc" className="block text-gray-700 font-medium">Edgemarc</label>
          <select
            id="edgemarc"
            name="edgemarc"
            value={values.edgemarc}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded-md shadow-sm"
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </div>

        {values.edgemarc === 'yes' && (
          <div className="mb-4 p-4 bg-lime-500 rounded-md">
            <h3 className="text-lg font-semibold mb-2">WAN Information</h3>
            <input
              type="text"
              name="em_mac_address"
              value={values.em_mac_address}
              onChange={handleChange}
              placeholder="Edgemarc MAC Address"
              className="w-full px-3 py-2 border rounded-md shadow-sm mb-2"
            />
            <input
              type="text"
              name="wan1"
              value={values.wan1}
              onChange={handleChange}
              placeholder="WAN 1 - IP Address"
              className="w-full px-3 py-2 border rounded-md shadow-sm mb-2"
            />
            <input
              type="text"
              name="wan2"
              value={values.wan2}
              onChange={handleChange}
              placeholder="WAN 2 - IP Address"
              className="w-full px-3 py-2 border rounded-md shadow-sm mb-2"
            />
            <input
              type="text"
              name="eth"
              value={values.eth}
              onChange={handleChange}
              placeholder="ETH"
              className="w-full px-3 py-2 border rounded-md shadow-sm"
            />
          </div>
        )}

        {/* Company Address */}
        <div className="mb-4">
          <label htmlFor="company_address" className="block text-gray-700 font-medium">Company Address</label>
          <input
            id="company_address"
            type="text"
            name="company_address"
            value={values.company_address}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded-md shadow-sm"
            placeholder="1234 Main St, City, Country"
            required
          />
        </div>

        {/* Platform Toggle */}
        <div className="mb-4">
          <span className="block text-gray-700 font-medium mb-2">Platform</span>
          <div className="flex" role="group" aria-label="Platform selection">
            <button
              type="button"
              onClick={() => setValues(prev => ({ ...prev, platform: 'broadsoft' }))}
              className={`px-4 py-2 border rounded-l-md ${
                values.platform === 'broadsoft'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
              aria-pressed={values.platform === 'broadsoft'}
            >
              Broadsoft
            </button>
            <button
              type="button"
              onClick={() => setValues(prev => ({ ...prev, platform: 'bicom' }))}
              className={`px-4 py-2 border rounded-r-md ${
                values.platform === 'bicom'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
              aria-pressed={values.platform === 'bicom'}
            >
              Bicom
            </button>
          </div>
        </div>

        {/* Platform Details - Bicom */}
        {values.platform === 'bicom' && (
          <div className="mb-4">
            <label htmlFor="server" className="block text-gray-700 font-medium mb-2">Server</label>
            
            {/* Server Group Tabs */}
            <div className="flex mb-2 overflow-x-auto">
              {Object.keys(ALL_BICOM_SERVERS).map(group => (
                <button
                  key={group}
                  type="button"
                  onClick={() => {
                    setSelectedServerGroup(group);
                    setServerFilterText('');
                  }}
                  className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
                    selectedServerGroup === group
                      ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-500'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {group}
                </button>
              ))}
            </div>

            {/* Server Filter */}
            <div className="relative mb-2">
              <input
                type="text"
                placeholder={`Filter ${selectedServerGroup} servers...`}
                value={serverFilterText}
                onChange={(e) => setServerFilterText(e.target.value)}
                className="w-full px-3 py-2 border rounded-md shadow-sm"
              />
              {serverFilterText && (
                <button
                  type="button"
                  onClick={() => setServerFilterText('')}
                  className="absolute right-3 top-2 text-gray-500 hover:text-gray-700"
                  aria-label="Clear filter"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Server Selection */}
            <select
              id="server"
              name="server"
              value={values.server}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-md shadow-sm"
              required
            >
              <option value="">Select a Server</option>
              {filteredServers.length === 0 ? (
                <option disabled>No matching servers found</option>
              ) : (
                filteredServers.map((server, index) => (
                  <option key={index} value={server}>
                    {server}
                  </option>
                ))
              )}
            </select>
          </div>
        )}

        {/* Broadsoft Details */}
        {values.platform === 'broadsoft' && (
          <div className="mb-4">
            <label htmlFor="enterprise" className="block text-gray-700 font-medium">Enterprise</label>
            <input
              id="enterprise"
              type="text"
              name="enterprise"
              value={values.enterprise}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-md shadow-sm"
              placeholder="Broadsoft Enterprise"
              required
            />
          </div>
        )}

        {/* ISP - Circuit ID */}
        <div className="mb-4">
          <label htmlFor="isp_circuit_id" className="block text-gray-700 font-medium">ISP - Circuit ID</label>
          <input
            id="isp_circuit_id"
            type="text"
            name="isp_circuit_id"
            value={values.isp_circuit_id}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded-md shadow-sm"
            placeholder="ISP Circuit ID"
            required
          />
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isLoading}
            className={`px-12 py-2 bg-green-600 text-white font-semibold border-b-4 rounded hover:bg-green-500 border-green-800 hover:border-green-700 flex items-center ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : (
              id ? 'Update Customer' : 'Submit'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddCustomers;